'use client';

import { isToday, isYesterday, subMonths, subWeeks } from 'date-fns';
import Link from 'next/link';
import { useParams, usePathname, useRouter } from 'next/navigation';
import type { User } from 'next-auth';
import { memo, useEffect, useState } from 'react';
import { toast } from 'sonner';
import useSWR from 'swr';

import {
  CheckCircleFillIcon,
  GlobeIcon,
  LockIcon,
  MoreHorizontalIcon,
  ShareIcon,
  TrashIcon,
} from '@/components/icons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import type { Chat } from '@/lib/db/schema';
import { fetcher, isEn } from '@/lib/utils';
import { useChatVisibility } from '@/hooks/use-chat-visibility';
import { sslog } from '@/lib/utils/sslog';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './tailwind-chat/ui/dialog';
import { ScrollArea } from './tailwind-chat/ui/scroll-area';
import TailwindAdvancedEditor from './tailwind-chat/advanced-editor';
import { Info } from 'lucide-react';
import ChatEditorDialog from './zustom/ChatEditorDialog';
// import useGlobalStore from '@/stores/global-store';

export type UIChat = Chat & { index?: number };

type GroupedChats = {
  today: UIChat[];
  yesterday: UIChat[];
  lastWeek: UIChat[];
  lastMonth: UIChat[];
  older: UIChat[];
};

const PureChatItem = ({
  spaceTag,
  chat,
  isActive,
  onDelete,
  onShowInfo,
  setOpenMobile,
}: {
  spaceTag: string;
  chat: Chat;
  isActive: boolean;
  onDelete: (chatId: string) => void;
  onShowInfo: (chatId: string) => void;
  setOpenMobile: (open: boolean) => void;
}) => {
  const { visibilityType, setVisibilityType } = useChatVisibility({
    chatId: chat.id,
    initialVisibility: chat.visibility,
  });

  // const { selectedChat, setSelectedChat } = useGlobalStore();

  return (
    <SidebarMenuItem>
      {/* <SidebarMenuButton asChild isActive={isActive}>
        <Link href={`/chat/${chat.id}`} onClick={() => setOpenMobile(false)}>
          <span>{chat.title}</span>
        </Link>
      </SidebarMenuButton> */}

      <SidebarMenuButton asChild isActive={isActive}>
        <Link href={`/${spaceTag?spaceTag:'chat'}/${chat.id}`} onClick={() => { 
          setOpenMobile(false);
          // setSelectedChat(chat.id); 
        }}>
          <span>{chat.title}</span>
        </Link>
      </SidebarMenuButton>

      <DropdownMenu modal={true}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground mr-0.5"
            showOnHover={!isActive}
          >
            <MoreHorizontalIcon />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="bottom" align="end">
          <DropdownMenuItem
            className="cursor-pointer flex-row  "
            onSelect={() => onShowInfo(chat.id)}
          >
            <Info />
            <span>Info</span>
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="cursor-pointer">
              <ShareIcon />
              <span>Share</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  className="cursor-pointer flex-row justify-between"
                  onClick={() => {
                    setVisibilityType('private');
                  }}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <LockIcon size={12} />
                    <span>Private</span>
                  </div>
                  {visibilityType === 'private' ? (
                    <CheckCircleFillIcon />
                  ) : null}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer flex-row justify-between"
                  onClick={() => {
                    setVisibilityType('public');
                  }}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <GlobeIcon />
                    <span>Public</span>
                  </div>
                  {visibilityType === 'public' ? <CheckCircleFillIcon /> : null}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuItem
            className="cursor-pointer text-destructive focus:bg-destructive/15 focus:text-destructive dark:text-red-500"
            onSelect={() => onDelete(chat.id)}
          >
            <TrashIcon />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};

export const ChatItem = memo(PureChatItem, (prevProps, nextProps) => {
  if (prevProps.isActive !== nextProps.isActive) return false;
  if (prevProps.chat !== nextProps.chat || prevProps.chat.title !== nextProps.chat.title) return false;
  return true;
});

// export const ChatItem = PureChatItem;

export function SidebarHistory({ user, search }: { user: User | undefined, search?: string }) {
  const { setOpenMobile, spaceTag, shareChatMsg } = useSidebar();
  sslog('updatedTitle -- SidebarHistory shareChatMsg:', shareChatMsg);
  const { id } = useParams();
  const pathname = usePathname();
  const {
    data: history,
    isLoading,
    mutate,
  } = useSWR<Array<Chat>>(user ? '/api/history' : null, fetcher, {
    fallbackData: [],
    revalidateOnFocus: true, // 确保焦点时重新验证
    revalidateOnReconnect: true, // 确保重新连接时重新验证
  });

  sslog('updatedTitle history.titles:', history?.map(chat=>chat.title));
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [updateChat, setUpdateChat] = useState<Chat | null>(null);

  const router = useRouter();
  const handleDelete = async () => {
    const deletePromise = fetch(`/api/chat?id=${deleteId}`, {
      method: 'DELETE',
    });

    toast.promise(deletePromise, {
      loading: 'Deleting chat...',
      success: () => {
        mutate((history) => {
          if (history) {
            return history.filter((h) => h.id !== id);
          }
        });
        return 'Chat deleted successfully';
      },
      error: 'Failed to delete chat',
    });

    setShowDeleteDialog(false);

    if (deleteId === id) {
      router.push('/');
    }
  };

  if (!user) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="px-2 text-zinc-500 w-full flex flex-row justify-center items-center text-sm gap-2">
            Login to save and revisit previous chats!
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (isLoading) {
    return (
      <SidebarGroup>
        <div className="px-2 py-1 text-xs text-sidebar-foreground/50">
          Today
        </div>
        <SidebarGroupContent>
          <div className="flex flex-col">
            {[44, 32, 28, 64, 52].map((item) => (
              <div
                key={item}
                className="rounded-md h-8 flex gap-2 px-2 items-center"
              >
                <div
                  className="h-4 rounded-md flex-1 max-w-[--skeleton-width] bg-sidebar-accent-foreground/10"
                  style={
                    {
                      '--skeleton-width': `${item}%`,
                    } as React.CSSProperties
                  }
                />
              </div>
            ))}
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (history?.length === 0) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="px-2 text-zinc-500 w-full flex flex-row justify-center items-center text-sm gap-2">
            Your conversations will appear here once you start chatting!
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  const groupChatsByDate = (chats: Chat[]): GroupedChats => {
    const now = new Date();
    const oneWeekAgo = subWeeks(now, 1);
    const oneMonthAgo = subMonths(now, 1);
    let index = 0;
    
    return chats.reduce(
      (groups, chat) => {
        const chatDate = new Date(chat.createdAt);
        // 确保创建新的 chat 对象，而不是直接使用原对象
        const chatWithIndex = { ...chat, index };

        if (isToday(chatDate)) {
          groups.today.push(chatWithIndex);
        } else if (isYesterday(chatDate)) {
          groups.yesterday.push(chatWithIndex);
        } else if (chatDate > oneWeekAgo) {
          groups.lastWeek.push(chatWithIndex);
        } else if (chatDate > oneMonthAgo) {
          groups.lastMonth.push(chatWithIndex);
        } else {
          groups.older.push(chatWithIndex);
        }
        index++;
        return groups;
      },
      {
        today: [],
        yesterday: [],
        lastWeek: [],
        lastMonth: [],
        older: [],
      } as GroupedChats,
    );
  };

  return (
    <>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {history &&
              (() => {
                const groupedChats = groupChatsByDate(history);

                return (
                  <>
                    {groupedChats.today.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs text-sidebar-foreground/50">
                          Today
                        </div>
                        {groupedChats.today.map((chat) => (
                          <ChatItem
                            spaceTag={spaceTag}
                            key={chat.id}
                            chat={chat}
                            isActive={chat.id === id}
                            onDelete={(chatId) => {
                              setDeleteId(chatId);
                              setShowDeleteDialog(true);
                            }}
                            onShowInfo={(chatId)=>{
                              setUpdateChat(chat);
                              setShowInfoDialog(true);
                            }}
                            setOpenMobile={setOpenMobile}
                          />
                        ))}
                      </>
                    )}

                    {groupedChats.yesterday.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs text-sidebar-foreground/50 mt-6">
                          Yesterday
                        </div>
                        {groupedChats.yesterday.map((chat) => (
                          <ChatItem
                            spaceTag={spaceTag}
                            key={chat.id}
                            chat={chat}
                            isActive={chat.id === id}
                            onDelete={(chatId) => {
                              setDeleteId(chatId);
                              setShowDeleteDialog(true);
                            }}
                            onShowInfo={(chatId)=>{
                              setUpdateChat(chat);
                              setShowInfoDialog(true);
                            }}
                            setOpenMobile={setOpenMobile}
                          />
                        ))}
                      </>
                    )}

                    {groupedChats.lastWeek.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs text-sidebar-foreground/50 mt-6">
                          Last 7 days
                        </div>
                        {/* {groupedChats.lastWeek.map((chat) => ( */}
                        {(search && search?.length > 11110 ? groupedChats.lastWeek.filter(c=>c.title.includes(search)) : groupedChats.lastWeek).map((chat) => (
                          <ChatItem
                            spaceTag={spaceTag}
                            key={chat.id}
                            chat={chat}
                            isActive={chat.id === id}
                            onDelete={(chatId) => {
                              setDeleteId(chatId);
                              setShowDeleteDialog(true);
                            }}
                            onShowInfo={(chatId)=>{
                              setUpdateChat(chat);
                              setShowInfoDialog(true);
                            }}
                            setOpenMobile={setOpenMobile}
                          />
                        ))}
                      </>
                    )}

                    {groupedChats.lastMonth.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs text-sidebar-foreground/50 mt-6">
                          Last 30 days
                        </div>
                        
                        {/* {(search ? groupedChats.lastMonth.filter(c=>c.title.includes(search)) : groupedChats.lastMonth).map((chat) => ( */}
                          {groupedChats.lastMonth.map((chat) => ( 
                          <ChatItem
                            spaceTag={spaceTag}
                            key={chat.id}
                            chat={chat} //{chat.index ? history[chat.index] : chat}
                            isActive={chat.id === id}
                            onDelete={(chatId) => {
                              setDeleteId(chatId);
                              setShowDeleteDialog(true);
                            }}
                            onShowInfo={(chatId)=>{
                              setUpdateChat(chat);
                              setShowInfoDialog(true);
                            }}
                            setOpenMobile={setOpenMobile}
                          />
                        ))}
                      </>
                    )}

                    {groupedChats.older.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs text-sidebar-foreground/50 mt-6">
                          Older
                        </div>
                        {/* {groupedChats.older.map((chat) => (  */}
                        {(search && search.length > 0  ? groupedChats.older.filter(c=>c.title.includes(search)) : groupedChats.older).map((chat) => (
                        
                          <ChatItem
                            spaceTag={spaceTag}
                            key={chat.id}
                            chat={chat.index ? history[chat.index] : chat}
                            isActive={chat.id === id}
                            onDelete={(chatId) => {
                              setDeleteId(chatId);
                              setShowDeleteDialog(true);
                            }}
                            onShowInfo={(chatId)=>{
                              setUpdateChat(chat);
                              setShowInfoDialog(true);
                            }}
                            setOpenMobile={setOpenMobile}
                          />
                        ))}
                      </>
                    )}
                  </>
                );
              })()}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isEn ? 'Are you absolutely sure?':'确定删除此会话？'}</AlertDialogTitle>
            <AlertDialogDescription>{isEn ? 'This action cannot be undone. This will permanently delete your chat and remove it from our servers.'
              :'这条会话将被永久删除，不可恢复及撤销!'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isEn ? 'Cancel':'取消'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              {isEn ? 'Continue':'删除'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ChatEditorDialog showInfoDialog={showInfoDialog} setShowInfoDialog={setShowInfoDialog} updateChat={updateChat} shareChatMsg={shareChatMsg}
        onSaveSuccess={(newInfo) => {
          // 更新列表数据 // 1. 内存中更新，UI 瞬间响应，不消耗带宽
          mutate((currentHistory) => {
            if (!currentHistory) return [];
            return currentHistory.map((chat) => 
              chat.id === updateChat?.id ? { ...chat, info: newInfo } : chat
            );
          }, false);
          
          // 更新当前选中项状态
          setUpdateChat((prev) => prev ? { ...prev, info: newInfo } : null);

          // 2. (可选) 如果你担心多端同步问题，可以在 mutate 后触发一次后台静默验证
          // mutate(); // 不带 false 参数，会发起请求核对数据
        }} 
      />

      {/* 这里的 asChild 很重要，或者直接把 DialogDescription 换成 div，
          因为 Description 默认是 p 标签，里面不能放 pre (块级元素)//  {/* 核心：null, 2 用于缩进 */} 
      {/* <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogTitle>{updateChat?.title}</DialogTitle>
        
        <DialogContent className="flex flex-col max-w-3xl h-[calc(100vh-24px)]">

          <DialogDescription asChild>
            <div className="flex-1 overflow-hidden">
              <div className="h-full w-full overflow-auto rounded-md bg-slate-950 p-4 text-xs text-white">
                <pre>
                 
                  <code>{JSON.stringify(updateChat, null, 2)}</code>
                </pre>
              </div>
            </div>
          </DialogDescription>
        </DialogContent>
      </Dialog> */}
    </>
  );
}
